import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { IconButton, Tooltip } from '@mui/material';
import { Reply as ReplyIcon, Edit as EditIcon, UploadFile as UploadFileIcon } from '@mui/icons-material';
import PerfectScrollbar from 'react-perfect-scrollbar';

import Map from '../../components/Map/Map';
import Fader from '../../components/Loaders/Fader';
import CaseForm from '../../components/CaseForm/CaseForm';
import ReplyModal from '../../components/ReplyModal/ReplyModal';

import { getCaseInfo } from '../../../http/caseAPI';
import { getRidingInfo } from '../../../http/ridingAPI';
import { getPollData, getPollInfo, getRidingData } from '../../../http/pollsAPI';
import { PARTIES_COLORS, PROVINCIAL_RESULTS, USER_ROLES } from '../../../util/constants/AppConstants';
import { checkForEmptyString, formatNumberWithCommas, formatUnixDate } from '../../../util/HelperFunctions';
import { setNotify } from '../../../datastore/actions/actionActions';

import ChevronIcon from '../../../assets/images/icons/chevron.svg';
import CopyIcon from '../../../assets/images/icons/copy.svg';
import './CasePage.scss';
import TagWindow from '../../components/TagWindow/TagWindow';

const CasePage = () => {
  const dispatch = useDispatch();

  const config = useSelector((state) => state.config.config);

  const [caseData, setCaseData] = useState(null);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [widgets, setWidgets] = useState([]);
  const [pollId, setPollId] = useState(false);
  const [pollData, setPollData] = useState(null);
  const [wardId, setWardId] = useState(null);
  const [ridingData, setRidingData] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isReplyModalOpen, setIsReplyModalOpen] = useState(false);
  const [isTagWindowOpen, setIsTagWindowOpen] = useState(false);

  const { userInfo } = useOutletContext();

  const threadRef = useRef(null);

  const { id } = useParams();

  const navigate = useNavigate();

  const pollIdExists = pollId !== 'NaN';

  const mppRoleValue = USER_ROLES.find((role) => role.short === 'MPP').value;
  const councillorRoleValue = USER_ROLES.find((role) => role.short === 'Councillor').value;

  const isMpp = userInfo && userInfo.role === mppRoleValue;
  const isCouncillor = userInfo && userInfo.role === councillorRoleValue;

  useEffect(() => {
    if (threadRef.current) {
      const container = threadRef.current._container;

      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }
  }, [caseData?.responses]);

  const handleGetRidingInfo = async (mppId) => {
    const res = await getRidingInfo(config, mppId);

    if (res.status === 200) {
      setWidgets(res.data.riding.widgets.sort((a, b) => a.id - b.id));
    } else {
      console.log('ERROR: Grabbing Widgets');
    }
  };

  const handleGetPollData = async (pollId) => {
    const res = await getPollData(config, userInfo.id, pollId);

    if (res.status === 200) {
      setPollData(res.data.demographics ?? []);
    } else {
      console.log('ERROR: Grabbing Poll Data');
    }
  };

  const handleGetRidingData = async (ridingId) => {
    const res = await getRidingData(config, ridingId);

    if (res.status === 200) {
      setRidingData(res.data.riding);
    } else {
      console.log('ERROR: Grabbing Riding Data');
    }
  };

  const handleGetPollInfo = async (postalCode) => {
    const res = await getPollInfo(config, postalCode);

    if (res.status === 200) {
      if (isCouncillor) {
        if (!res.data.rows.length || !res.data.rows[0].ward_id) {
          setWardId('NaN');
          // handleGetCityData(userInfo.city);
          return;
        }

        setWardId(res.data.rows[0].ward_id);
        // handleGetCityData(userInfo.city);
      } else {
        if (!res.data.rows.length || !res.data.rows[0].poll_id) {
          setPollId('NaN');
          handleGetRidingData(userInfo.riding);
          return;
        }

        setPollId(res.data.rows[0].poll_id);
        handleGetPollData(res.data.rows[0].poll_id);
        handleGetRidingData(res.data.rows[0].riding_id);
      }
    } else {
      console.log('ERROR: Grabbing Poll information');
    }
  };

  const handleGetCaseInfo = async (id) => {
    const res = await getCaseInfo(config, userInfo.id, id);

    if (res.status === 200) {
      const caseData = res.data.case;

      if (caseData) {
        setCaseData(caseData);
        if (isCouncillor) {
          // setWidgets([
          //   { id: 1, name: 'Location' },
          //   { id: 2, name: 'Demographic Snapshot' },
          // ]);
        } else {
          handleGetRidingInfo(caseData.mpp_id);
        }
        handleGetPollInfo(caseData.postal_code);
      }
    } else {
      console.log('ERROR: Grabbing Case Info');
    }
  };

  useEffect(() => {
    if (config.api && userInfo && id) {
      handleGetCaseInfo(id);
    }
  }, [config, userInfo, id]);

  const handleReplyMessage = () => {
    if (!caseData.email) {
      dispatch(
        setNotify({
          status: true,
          type: '',
          title: 'Attention',
          message: 'Cannot send reply: constituent has no email',
        }),
      );
    } else {
      setIsReplyModalOpen(true);
    }
  };

  const handleEditMessage = () => {
    setIsEditModalOpen(true);
  };

  const handleCloseEditForm = () => {
    setIsEditModalOpen(false);
  };

  const handleOpenTagWindow = () => {
    setIsTagWindowOpen(true);
  };

  const handleCloseTagWindow = () => {
    setIsTagWindowOpen(false);
  };

  const handleCopy = (value, index) => {
    if (value !== '') {
      navigator.clipboard.writeText(value).then(() => {
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 1000);
      });
    }
  };

  if (!caseData) {
    return (
      <div className="wrap">
        <Fader />
      </div>
    );
  }

  return (
    <div className="message">
      <div className="message__header">
        <div className="message__back" onClick={() => navigate('/cases')}>
          <img src={ChevronIcon} alt="Back" />
          Back to Cases
        </div>
        <div className="message__control">
          <div className="message__btns">
            <Tooltip title="Reply">
              <IconButton className={`icon-button ${isMpp || isCouncillor ? 'blue' : ''}`} onClick={handleReplyMessage}>
                <ReplyIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton className={`icon-button ${isMpp || isCouncillor ? 'blue' : ''}`} onClick={handleEditMessage}>
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Upload files">
              <IconButton className={`icon-button ${isMpp || isCouncillor ? 'blue' : ''}`} onClick={() => {}}>
                <UploadFileIcon />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      </div>
      <div className="message__info">
        <div className="message__status">
          <span className="message__date">{formatUnixDate(caseData.date_utc, true)}</span>
        </div>
        <div className="message__wrap">
          <div className="message__block">
            <h2 className="message__title case">Case {caseData.case_id || '#'}</h2>
            <h2 className="message__title">{caseData.name || '(No name)'}</h2>
            <p className="message__text">{caseData.description ? caseData.description : '-'}</p>
          </div>
          <div className="message__block">
            <h2 className="message__title">Information</h2>
            <div className="message__information">
              <div className="message__line">
                <span>Address:</span>
                <span>{checkForEmptyString(caseData.address)}</span>
              </div>
              <div className="message__line">
                <span>Postal Code:</span>
                <span>{caseData.postal_code}</span>
              </div>
              <div className="message__line">
                <span>Email:</span>
                <span className="message__email-list">
                  {caseData.email
                    ? caseData.email.split(';').map((email, idx) => {
                        const trimmed = email.trim();
                        return (
                          <span
                            key={idx}
                            className={`message__email ${trimmed === '' ? 'empty' : ''}`}
                            onClick={() => handleCopy(trimmed, idx)}
                          >
                            {trimmed !== '' && (
                              <Tooltip title="Copy">
                                <img src={CopyIcon} alt="Copy" />
                              </Tooltip>
                            )}
                            {checkForEmptyString(trimmed)}
                            {copiedIndex === idx && <span className="message__notify">Email copied</span>}
                          </span>
                        );
                      })
                    : '-'}
                </span>
              </div>
              <div className="message__line">
                <span>Phone:</span>
                <span>{checkForEmptyString(caseData.phone)}</span>
              </div>
              <div className="message__line">
                <span>Contact Count:</span>
                <span>{caseData.contacts}</span>
              </div>
            </div>
            <h2 className="message__title">Category</h2>
            <span className="message__category">
              {caseData.type}
              {caseData.subtype && ` — ${caseData.subtype}`}
            </span>
            <h2 className="message__title">Tags</h2>
            <div className="tags">
              {caseData.tags.map((tag, i) => (
                <span key={i} className="tag">
                  {tag}
                </span>
              ))}
              <span
                className="tag tag-add"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenTagWindow();
                }}
              >
                +
              </span>
            </div>
            <div className="message__source-wrap">
              <div className="message__source">
                <h2 className="message__title">Source</h2>
                <div className="message__author">{caseData.case_source ?? 'No data'}</div>
              </div>
              <div className="message__source">
                <h2 className="message__title">Added by</h2>
                <div className="message__author">{caseData.staffer_fullname}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {caseData.responses && caseData.responses.length > 0 && (
        <div className="message__demographics thread">
          <h2 className="message__title">Email Thread</h2>
          <div className="message__thread-wrapper">
            <PerfectScrollbar
              ref={threadRef}
              options={{
                wheelPropagation: false,
                autoHide: false,
              }}
            >
              <div className="message__thread">
                {caseData.responses.map((resp) => (
                  <div
                    key={resp.id}
                    className={`message__reply ${resp.author_type === 'user' ? 'outgoing' : 'incoming'}`}
                  >
                    <div className="message__reply-header">
                      <span className="message__reply-author">{resp.author}</span>
                      <div className="message__reply-header-right">
                        <span className="message__reply-date">{formatUnixDate(resp.date_utc, true)}</span>
                        {resp.author_type === 'constituent' && (
                          <Tooltip title="Reply">
                            <IconButton
                              className={`icon-button ${isMpp || isCouncillor ? 'blue' : ''}`}
                              onClick={handleReplyMessage}
                            >
                              <ReplyIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                    <div className="message__reply-text">{resp.message}</div>
                  </div>
                ))}
              </div>
            </PerfectScrollbar>
          </div>
        </div>
      )}
      <div className="message__demographics">
        <h2 className="message__title">Member notes</h2>
        <p className={`message__text ${caseData.member_notes !== '' ? 'notes' : ''}`}>
          {caseData.member_notes !== '' ? caseData.member_notes : '-'}
        </p>
      </div>
      {widgets.length > 0 && (
        <div className="message__demographics">
          <h2 className="message__title">Know Your Riding — Opportunity Scorecard</h2>
          {(pollIdExists && pollData && ridingData) || (!pollIdExists && ridingData) ? (
            <div className={`scorecard ${pollIdExists ? 'col3' : 'col2'}`}>
              {widgets.map((widget, index) => (
                <div className="scorecard__block" key={index}>
                  <div className="scorecard__header">
                    <h3 className="scorecard__title">
                      {index + 1}. {widget.name}
                      {widget.id === 4 && ' (Top 3)'}
                    </h3>
                  </div>
                  {widget.id === 1 && (
                    <div className="scorecard__info">
                      <div className="scorecard__line">
                        <span className="scorecard__text">Electoral Unit:</span>
                        {pollIdExists && <span className="scorecard__text">Poll {pollData.poll_label}</span>}
                        <span className="scorecard__text">{ridingData.riding}</span>
                      </div>
                    </div>
                  )}
                  {widget.id === 2 && (
                    <>
                      <div className="scorecard__info">
                        <div className="scorecard__line">
                          <span className="scorecard__text">Population:</span>
                          {pollIdExists && (
                            <span className="scorecard__text">{formatNumberWithCommas(pollData.total_population)}</span>
                          )}
                          <span className="scorecard__text">{formatNumberWithCommas(ridingData.total_population)}</span>
                        </div>
                        <div className="scorecard__line">
                          <span className="scorecard__text">Average age:</span>
                          {pollIdExists && (
                            <span className="scorecard__text">
                              Male - {pollData.avg_age_male}; Female - {pollData.avg_age_female}
                            </span>
                          )}
                          <span className="scorecard__text">
                            Male - {ridingData.avg_age_male}; Female - {ridingData.avg_age_female}
                          </span>
                        </div>
                        <div className="scorecard__line">
                          <span className="scorecard__text">Male / Female ratio:</span>
                          {pollIdExists && (
                            <div className="scorecard__column">
                              <div className="chart double">
                                <div className="chart__labels">
                                  <span className="chart__label">Male ({pollData.male_percentage}%)</span>
                                  <span className="chart__label">Female ({pollData.female_percentage}%)</span>
                                </div>
                                <div className="chart__line">
                                  <div
                                    className="chart__progress"
                                    style={{ width: `${pollData.male_percentage}%` }}
                                  ></div>
                                  <div
                                    className="chart__progress"
                                    style={{ width: `${pollData.female_percentage}%` }}
                                  ></div>
                                </div>
                              </div>
                            </div>
                          )}
                          <div className="scorecard__column">
                            <div className="chart double">
                              <div className="chart__labels">
                                <span className="chart__label">Male ({ridingData.male_percentage}%)</span>
                                <span className="chart__label">Female ({ridingData.female_percentage}%)</span>
                              </div>
                              <div className="chart__line">
                                <div
                                  className="chart__progress"
                                  style={{ width: `${ridingData.male_percentage}%` }}
                                ></div>
                                <div
                                  className="chart__progress"
                                  style={{ width: `${ridingData.female_percentage}%` }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        </div>
                        {/* <div className="scorecard__line">
                          <span className="scorecard__text">Visible minority:</span>
                          <span className="scorecard__text">78%</span>
                        </div> */}
                        <div className="scorecard__line">
                          <span className="scorecard__text">Household size:</span>
                          {pollIdExists && (
                            <span className="scorecard__text">avg. {pollData.average_household_size} persons</span>
                          )}
                          <span className="scorecard__text">avg. {ridingData.average_household_size} persons</span>
                        </div>
                        {/* <div className="scorecard__line">
                          <span className="scorecard__text">Home ownership:</span>
                          <span className="scorecard__text">65% own; 35% rent</span>
                        </div> */}
                        {/* <div className="scorecard__line">
                          <span className="scorecard__text">Median income:</span>
                          <span className="scorecard__text">$82,000</span>
                        </div> */}
                        <div className="scorecard__line">
                          <span className="scorecard__text">Top occupations:</span>
                          {pollIdExists && (
                            <div className="scorecard__column">
                              <span className="scorecard__text">
                                {pollData.top_occupations.length > 0
                                  ? `1. ${pollData.top_occupations[0].label} (${pollData.top_occupations[0].percentage}%)`
                                  : '-'}
                              </span>
                              <span className="scorecard__text">
                                {pollData.top_occupations.length > 1
                                  ? `2. ${pollData.top_occupations[1].label} (${pollData.top_occupations[1].percentage}%)`
                                  : ''}
                              </span>
                            </div>
                          )}
                          <div className="scorecard__column">
                            <span className="scorecard__text">
                              {ridingData.top_occupations.length > 0
                                ? `1. ${ridingData.top_occupations[0].label} (${ridingData.top_occupations[0].percentage}%)`
                                : '-'}
                            </span>
                            <span className="scorecard__text">
                              {ridingData.top_occupations.length > 1
                                ? `2. ${ridingData.top_occupations[1].label} (${ridingData.top_occupations[1].percentage}%)`
                                : ''}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="scorecard__disclaimer">
                        * Data supplied by Esri and ArcGIS from November/December 2024
                      </div>
                    </>
                  )}
                  {widget.id === 3 && (
                    <>
                      <div className="scorecard__info">
                        <div className="scorecard__line">
                          <span className="scorecard__text">Party Affiliation:</span>
                          {pollIdExists && (
                            <div className="scorecard__column">
                              {pollData.party_results.map((party, i) => (
                                <div className="chart" key={i}>
                                  <span className="chart__label">
                                    {party.name} ({party.percentage}%)
                                  </span>
                                  <div className="chart__line">
                                    <div
                                      className="chart__progress"
                                      style={{
                                        width: `${party.percentage}%`,
                                        backgroundColor: `${PARTIES_COLORS.find((elem) => elem.id === party.id).color}`,
                                      }}
                                    ></div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="scorecard__column">
                            {ridingData.party_results.map((party, i) => (
                              <div className="chart" key={i}>
                                <span className="chart__label">
                                  {party.name} ({party.percentage}%)
                                </span>
                                <div className="chart__line">
                                  <div
                                    className="chart__progress"
                                    style={{
                                      width: `${party.percentage}%`,
                                      backgroundColor: `${PARTIES_COLORS.find((elem) => elem.party === party.name).color}`,
                                    }}
                                  ></div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="scorecard__line">
                          <span className="scorecard__text">Total votes:</span>
                          {pollIdExists && (
                            <span className="scorecard__text">{formatNumberWithCommas(pollData.votes)}</span>
                          )}
                          <span className="scorecard__text">{formatNumberWithCommas(ridingData.votes)}</span>
                        </div>
                      </div>
                      <div className="scorecard__disclaimer">* Data supplied by Elections Ontario from 2025</div>
                    </>
                  )}
                  {widget.id === 4 && (
                    <div className="scorecard__info">
                      <div className="scorecard__column">
                        {PROVINCIAL_RESULTS.map((party, i) => (
                          <div className="chart" key={i}>
                            <span className="chart__label">
                              {party.name} ({party.percentage}%)
                            </span>
                            <div className="chart__line">
                              <div
                                className="chart__progress"
                                style={{
                                  width: `${party.percentage}%`,
                                  backgroundColor: `${PARTIES_COLORS.find((elem) => elem.party === party.name).color}`,
                                }}
                              ></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <Fader />
          )}
        </div>
      )}
      <div className="message__demographics">
        <h2 className="message__title">{isCouncillor ? 'Know Your City — Ward Map' : 'Know Your Riding — Poll Map'}</h2>
        {((!isCouncillor && pollId === 'NaN') || (isCouncillor && wardId === 'NaN')) && (
          <div className="poll-alert">
            Postal Code Data processing for specific {isCouncillor ? 'ward' : 'poll'}. Check back soon
          </div>
        )}
        <div className="map__box">
          {isCouncillor ? (
            wardId ? (
              <Map wardId={wardId} isCouncillor={true} />
            ) : (
              <Fader />
            )
          ) : pollId ? (
            <Map pollId={pollId} />
          ) : (
            <Fader />
          )}
        </div>
      </div>
      {isEditModalOpen && (
        <CaseForm
          isOpen={isEditModalOpen}
          onCloseHandler={handleCloseEditForm}
          caseData={caseData}
          handleGetCaseData={() => handleGetCaseInfo(id)}
        />
      )}
      {isReplyModalOpen && (
        <ReplyModal
          isOpen={isReplyModalOpen}
          setIsOpen={setIsReplyModalOpen}
          caseId={caseData.case_id}
          recipientEmail={caseData.email.split(';')[0]}
          handleGetFeedbackData={() => handleGetCaseInfo(id)}
        />
      )}
      {isTagWindowOpen && (
        <TagWindow
          isOpen={isTagWindowOpen}
          onCloseHandler={handleCloseTagWindow}
          activeCase={caseData}
          handleGetCases={() => handleGetCaseInfo(id)}
        />
      )}
    </div>
  );
};

export default CasePage;
